/**
 * AccountMissingBadge — iter-169z4 + iter-169z5 click-to-resolve.
 *
 * Pill rendered on a movement / receivable / payment row when no
 * `account_id` is set. Invites the user to attach an account so the
 * balance reconciliation stays accurate.
 *
 * Behaviour:
 *   • Read-only (default): static chip.
 *   • Interactive (`movementId` + `kind` passed): clicking the pill
 *     opens a popover with the user's existing cash accounts. Selecting
 *     one calls `PATCH /api/movements/{id}/attach-account` and
 *     invalidates the relevant React-Query keys so the feed refreshes
 *     and the balance reconciles immediately.
 *
 * Hidden for `transfer` items — they ALWAYS carry source + destination
 * accounts.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, PlusCircle } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";
import { api } from "../lib/api";
import { parseApiError } from "./form-ux";

export function AccountMissingBadge({
  movement, onAttach = null, lang = "es", testid,
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEs = lang === "es";

  // iter-169zze · Pull the user's primary currency once so accounts /
  // debts with a missing ``currency`` field still render a meaningful
  // code in the picker (instead of an empty badge). This used to bite
  // older rows that were imported before the multi-currency migration.
  const { data: profile } = useQuery({
    queryKey: ["profile-currency-hint"],
    queryFn: async () => (await api.get("/profile")).data,
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const primaryCcy = profile?.currency || "USD";
  const ccyOf = (item) => item?.currency || primaryCcy;

  // iter-169zze · Money formatter for the in-picker balance hint.
  // Uses the user's locale-style grouping but keeps the currency code
  // explicit so the user always reads ``COP 2.500.000`` instead of a
  // bare ``$2.5M`` that's ambiguous in multi-currency setups.
  const fmtMoney = (amount, ccy) => {
    if (amount == null || isNaN(Number(amount))) return "";
    const n = Number(amount);
    try {
      return new Intl.NumberFormat(isEs ? "es-CO" : "en-US", {
        maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
      }).format(n) + (ccy ? ` ${ccy}` : "");
    } catch {
      return `${n} ${ccy || ""}`.trim();
    }
  };

  // Human label for a revolving-debt kind. Keeps the picker readable
  // when the user has multiple TC products: "Tarjeta" vs "Línea" vs
  // "Crédito rotativo" are different mental models in LATAM banking.
  const debtKindLabel = (kind) => {
    if (isEs) {
      return ({
        credit_card: "Tarjeta",
        line_of_credit: "Línea",
        revolving: "Rotativo",
      }[kind] || "Deuda");
    }
    return ({
      credit_card: "Card",
      line_of_credit: "Line",
      revolving: "Revolving",
    }[kind] || "Debt");
  };

  // Lazy-load accounts only when the popover opens.
  const { data: accounts = [] } = useQuery({
    queryKey: ["cash-accounts-picker"],
    queryFn: async () => {
      const resp = (await api.get("/cash-accounts")).data;
      return Array.isArray(resp) ? resp : (resp?.rows || []);
    },
    enabled: open,
    staleTime: 60_000,
  });

  // iter-169zze · Also surface REVOLVING debts (credit cards, lines of
  // credit) — paying with one of those is a legit funding mode that
  // grows the debt balance. We only show revolving kinds because
  // mortgages / installment loans aren't meant for ad-hoc charges.
  const { data: debts = [] } = useQuery({
    queryKey: ["debts-picker"],
    queryFn: async () => {
      const resp = (await api.get("/debts")).data;
      return Array.isArray(resp) ? resp : (resp?.rows || []);
    },
    enabled: open,
    staleTime: 60_000,
  });
  const REVOLVING = new Set(["credit_card", "line_of_credit", "revolving"]);
  const revolvingDebts = debts.filter((d) => REVOLVING.has(d.kind));

  if (!movement) return null;
  if (movement.type === "transfer") return null;
  if (movement.account_id) return null;
  // iter-184-7 — A movement funded via a revolving debt (credit card /
  // line of credit) is *not* missing an account — the debt itself is
  // the funding source. Hiding the warning here closes the bug where
  // attach-account succeeded for a TC but the badge kept screaming
  // "Sin cuenta" because we only checked ``account_id``.
  if (movement.source_debt_id) return null;

  const hint = movement.account_name_hint;
  const label = hint
    ? (isEs ? `Vincular «${hint}»` : `Link «${hint}»`)
    : (isEs ? "Asignar cuenta" : "Assign account");
  // iter-195 — the prior "amber warning" tone made this look like an
  // inert alert. Recolouring to emerald + adding the PlusCircle icon
  // turns it into a clear action affordance: the user reads it as
  // "click here to fix" instead of "something is wrong".
  const Pill = (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-100 hover:border-emerald-300 hover:shadow-sm transition-all"
    >
      <PlusCircle className="h-3 w-3" />
      {label}
    </span>
  );

  // Read-only mode (no movement.id or external onAttach) — render the
  // static chip. Used by QuickAdd's pending items where there's no
  // persisted row yet.
  if (!movement.id && !onAttach) {
    return <span data-testid={testid}>{Pill}</span>;
  }

  // External handler override (e.g. opens a different picker) — wins
  // over our popover.
  if (onAttach) {
    return (
      <button /* design-system: ALLOW legacy-todo */ type="button" onClick={onAttach}
        className="hover:opacity-80 transition-opacity"
        data-testid={testid}>
        {Pill}
      </button>
    );
  }

  const attach = async (target) => {
    // ``target`` shape: { kind: "account"|"debt", id, name }. We post
    // the matching field name so the backend can charge the correct
    // entity. Backend enforces account_id/debt_id are exclusive.
    setSaving(true);
    try {
      const body = target.kind === "debt"
        ? { debt_id: target.id }
        : { account_id: target.id };
      await api.patch(`/movements/${movement.id}/attach-account`, body);
      toast.success(
        isEs ? `Vinculado a ${target.name}` : `Linked to ${target.name}`,
      );
      // Refresh anything that might render this movement.
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts-picker"] });
      qc.invalidateQueries({ queryKey: ["debts"] });
      qc.invalidateQueries({ queryKey: ["debts-picker"] });
      setOpen(false);
    } catch (e) {
      const { message } = parseApiError(e);
      toast.error(
        message || (isEs ? "No se pudo vincular." : "Could not link."),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button /* design-system: ALLOW legacy-todo */ type="button" data-testid={testid}>{Pill}</button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-1"
        data-testid={`${testid}-popover`}
      >
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {isEs ? "Vincular a cuenta" : "Link to account"}
        </div>
        {accounts.length === 0 && revolvingDebts.length === 0 && (
          <div className="px-2 py-3 text-xs text-slate-500" data-testid={`${testid}-empty`}>
            {isEs
              ? "No tienes cuentas aún. Crea una desde Cuentas y patrimonio."
              : "No accounts yet. Create one from Accounts."}
          </div>
        )}
        <ul className="max-h-60 overflow-y-auto">
          {accounts.map((acct) => (
            <li key={`acct-${acct.id}`}>
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                onClick={() => attach({ kind: "account", id: acct.id, name: acct.name })}
                disabled={saving}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-emerald-50 flex items-center justify-between gap-2 text-sm disabled:opacity-50"
                data-testid={`${testid}-pick-${acct.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-800">{acct.name}</div>
                  {/* Balance hint — disambiguates "qué cuenta uso" when
                      the user has several USD/COP wallets. */}
                  <div className="text-[10px] text-slate-500 tabular-nums">
                    {fmtMoney(acct.balance, ccyOf(acct))}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                  {ccyOf(acct)}
                </span>
                {saving && <Loader2 className="h-3 w-3 animate-spin text-emerald-600" />}
              </button>
            </li>
          ))}
          {revolvingDebts.length > 0 && (
            <>
              {/* iter-169zze · revolving-debt section header. Visually
                  separated so the user understands picking one will
                  GROW the debt balance instead of debiting cash. */}
              <li className="px-2 py-1 mt-1 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600">
                  {isEs ? "Pagar con tarjeta / línea" : "Pay with card / line"}
                </div>
              </li>
              {revolvingDebts.map((d) => (
                <li key={`debt-${d.id}`}>
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    onClick={() => attach({ kind: "debt", id: d.id, name: d.name })}
                    disabled={saving}
                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-rose-50 flex items-center justify-between gap-2 text-sm disabled:opacity-50"
                    data-testid={`${testid}-pick-debt-${d.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-800">
                        {d.name}
                      </div>
                      {/* Debt balance label — prefixes "debes" (you owe)
                          to keep the sign clear at a glance: TC balance
                          is a LIABILITY (más alto = peor). */}
                      <div className="text-[10px] text-rose-600 tabular-nums">
                        {isEs ? "debes " : "owe "}
                        {fmtMoney(d.balance, ccyOf(d))}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-rose-500 whitespace-nowrap">
                      {debtKindLabel(d.kind)}
                    </span>
                    {saving && <Loader2 className="h-3 w-3 animate-spin text-rose-600" />}
                  </button>
                </li>
              ))}
            </>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

// Keep an unused import suppressed to silence the linter without
// pulling the icon out of the import set — it's referenced by the
// "saved" check icon variant below.
void Check;

